import { Button, Checkbox } from '@acpaas-ui/react-components';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { checkAuthExpiration, fetchUser } from './actions/auth.actions';
import PrivateRoute from './components/auth/PrivateRoute';
import Footer from './components/layout/Footer';
import Header from './components/layout/Header';
import NotFound from './components/NotFound';
import {
  AUTH_ERROR_PATH,
  LOGIN_CHOOSE_METHOD_PATH,
  LOGIN_FIRST_TIME_SETUP_PATH,
  LOGIN_PATH,
  LOGOUT_PATH,
  NOT_FOUND_PATH,
  NO_AUTH_PATHS,
  NO_PERMISSION_PATH,
  ROOT_PATH
} from './constants/paths.constants';
import { PERMISSIONS } from './constants/permissions.constants';
import { ROUTES } from './constants/routes.constants';
import { APPLICATION_BODY_ID } from './constants/settings.constants';
import AuthError from './containers/auth/AuthError';
import Login from './containers/auth/Login';
import LoginChooseMethod from './containers/auth/LoginChooseMethod';
import LoginFirstTimeSetup from './containers/auth/LoginFirstTimeSetup';
import Logout from './containers/auth/Logout';
import NoPermission from './containers/auth/NoPermission';
import Home from './containers/Home';
import { USER_RESOURCE } from './resources/user.resource';
import { authenticatedSelector, currentUserSelector } from './selectors/auth.selectors';
import { fetchedSelector, fetchingSelector } from './selectors/fetch.selectors';
import { getToken } from './services/session.storage';

toast.configure({ position: 'bottom-right', hideProgressBar: true, newestOnTop: true });

function Application() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const user = useSelector(currentUserSelector);
  const authenticated = useSelector((state) => authenticatedSelector(state, { path: location.pathname }));
  const isFetchingUser = useSelector((state) => fetchingSelector(state, { resource: USER_RESOURCE }));
  const isFetchedUser = useSelector((state) => fetchedSelector(state, { resource: USER_RESOURCE }));

  useEffect(() => {
    if (getToken() && !isFetchedUser && !isFetchingUser && location.pathname !== LOGIN_PATH) {
      dispatch(
        fetchUser((profile) => {
          if (profile?.user?.isFirstLogin) {
            return navigate(LOGIN_FIRST_TIME_SETUP_PATH);
          }
        })
      );
    }
  }, [dispatch, navigate, location.pathname, isFetchedUser, isFetchingUser]);

  useEffect(() => {
    const requiresAuth = !NO_AUTH_PATHS.some((path) => matchPath(path, location.pathname));
    if (requiresAuth && !authenticated) {
      navigate(`${LOGIN_CHOOSE_METHOD_PATH}?fromUrl=${location.pathname}${location.search || ''}`);
    }
  }, [navigate, location, authenticated]);

  useEffect(() => {
    const intervalId = setInterval(() => dispatch(checkAuthExpiration()), 10 * 1000);

    return () => clearInterval(intervalId);
  }, [dispatch]);

  const [devExpanded, setDevExpanded] = useState(false);

  return (
    <div id="js-spo-application-layout" className="spo-p-application__layout u-full-height">
      <Header user={user} authenticated={authenticated} />
      <main id={APPLICATION_BODY_ID} className={`spo-p-application__body u-bg-light`}>
        <div className="spo-p-application__container u-container">
          <Routes>
            <Route path={`${ROOT_PATH}`} element={<Home />}>
              {ROUTES.map((x) => (
                <Route
                  key={x.path}
                  path={`${x.path}/*`}
                  element={
                    <PrivateRoute
                      component={x.component}
                      access={authenticated}
                      user={user}
                      isFetchingUser={isFetchingUser}
                      isFetchedUser={isFetchedUser}
                      permission={x.permission}
                      orPermissions={x.orPermissions ?? []}
                      isPublic={x.isPublic}
                    />
                  }
                />
              ))}
            </Route>
            <Route path={`${LOGIN_FIRST_TIME_SETUP_PATH}`} element={<LoginFirstTimeSetup />} />
            <Route path={`${LOGIN_CHOOSE_METHOD_PATH}`} element={<LoginChooseMethod />} />
            <Route path={`${LOGIN_PATH}/*`} element={<Login />} />
            <Route path={`${LOGOUT_PATH}/*`} element={<Logout />} />
            <Route path={`${AUTH_ERROR_PATH}/*`} element={<AuthError />} />
            <Route path={`${NO_PERMISSION_PATH}/*`} element={<NoPermission />} />
            <Route path={`${NOT_FOUND_PATH}/*`} element={<NotFound />} />
            <Route path={'/*'} element={<PrivateRoute user={user} access={true} component={NotFound} />} />
          </Routes>
        </div>
      </main>
      <Footer />
      {/* keeping it for dev purposes, should be ALWAYS commented on REPO */}
      {/* <div
        style={{
          position: 'fixed',
          top: '0.75rem',
          right: '18rem',
          background: 'grey',
          color: 'white',
          padding: '0.75rem',
          paddingTop: 0,
          overflowY: 'hidden',
          height: devExpanded ? 'auto' : '24px'
        }}
      >
        <div className="u-margin-bottom-xs">
          <Button
            onClick={() => setDevExpanded(!devExpanded)}
            icon="ai-move-expand-vertical"
            className="u-margin-right-sm"
            size="tiny"
          ></Button>
          --dev only--
        </div>
        {Object.keys(PERMISSIONS).map((key) => (
          <Checkbox
            key={key}
            label={key}
            id={`checkbox-permission-${key}`}
            name={`checkbox-permission-${key}`}
            checked={user?.permissions?.includes(PERMISSIONS[key]) ?? false}
            onChange={() => dispatch({ type: 'DEV_AUTH_UPDATE_PERMISSIONS', permission: PERMISSIONS[key] })}
          />
        ))}
      </div> */}
    </div>
  );
}

export default Application;
